import { useState } from "react";
import { Box, IconButton, Typography, Paper, Tooltip, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccessibilityNewIcon from "@mui/icons-material/AccessibilityNew";
import { useLanguage } from "../Context/LangContext";

const cardStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    padding: "14px 10px",
    borderRadius: "10px",
    background: "#f4f5f7",
    border: "2px solid transparent",
    cursor: "pointer",
    transition: "all 0.2s ease",
    userSelect: "none",
    flex: 1,
    "&:hover": {
        background: "#e8eaf0",
        borderColor: "#1a2e5a",
    },
    "&.active": {
        background: "#dce3f0",
        borderColor: "#1a2e5a",
    },
};

const CardButton = ({ icon, label, onClick, active }) => (
    <Box
        onClick={onClick}
        sx={{
            ...cardStyle,
            borderColor: active ? "#1a2e5a" : "transparent",
            background: active ? "#dce3f0" : "#f4f5f7",
        }}
    >
        <Typography sx={{ fontSize: "22px", lineHeight: 1, fontFamily: "Inter400" }}>{icon}</Typography>
        <Typography
            sx={{
                fontSize: "12px",
                fontWeight: 700,
                fontFamily: "Inter500",
                color: "#1a2e5a",
                letterSpacing: "0.3px",
                textAlign: "center",
            }}
        >
            {label}
        </Typography>
    </Box>
);

export default function AccessibilityWidget() {
    const { increaseFont, decreaseFont, resetFont, voiceEnabled, toggleVoice } = useLanguage();
    const [open, setOpen] = useState(false);
    const [increased, setIncreased] = useState(false);
    const [decreased, setDecreased] = useState(false);



    const handleIncrease = () => {
        increaseFont();
        setIncreased((p) => !p);
        setDecreased(false);
    };

    const handleDecrease = () => {
        decreaseFont();
        setDecreased((p) => !p);
        setIncreased(false);
    };

    return (
        <Box
            sx={{
                position: "fixed",
                right: 24,
                bottom: 62,
                zIndex: 9999,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
            }}
        >
            {/* Panel */}
            {open && (
                <Paper
                    elevation={8}
                    sx={{
                        mb: 2,
                        borderRadius: "16px",
                        overflow: "hidden",
                        width: "310px",
                        boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
                        animation: "fadeSlideUp 0.22s ease",
                        "@keyframes fadeSlideUp": {
                            from: { opacity: 0, transform: "translateY(16px)" },
                            to: { opacity: 1, transform: "translateY(0)" },
                        },
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            background: "#5469A0",
                            px: 2.5,
                            py: 1.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography
                            sx={{
                                color: "#fff",
                                fontWeight: 700,
                                fontSize: "15px",
                                fontFamily:"Inter500",
                                letterSpacing: "0.4px",
                            }}
                        >
                            Accessibility Settings
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => setOpen(false)}
                            sx={{
                                color: "#fff",
                                border: "2px solid #c0392b",
                                borderRadius: "6px",
                                p: "2px",
                                "&:hover": { background: "#c0392b" },
                            }}
                        >
                            <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <Box sx={{ p: 2.5, background: "#fff" }}>

                        <Typography
                            sx={{
                                fontSize: "10px",
                                fontWeight: 800,
                                fontFamily: "cardiuma-SemiBold",
                                color: "#888",
                                letterSpacing: "1px",
                                textTransform: "uppercase",
                                mb: 1.5,
                            }}
                        >
                            Audio Tool
                        </Typography>

                        <Box sx={{ display: "flex", gap: 1.5, width: "100%", mb: 1.5 }}>
                            <CardButton
                                icon="🔊"
                                label={voiceEnabled ? "Screen Reader OFF" : "Screen Reader ON"}
                                onClick={toggleVoice}
                                active={voiceEnabled}
                            />
                        </Box>
                        <Typography
                            sx={{
                                fontSize: "10px",
                                fontWeight: 800,
                                fontFamily: "Inter500",
                                color: "#888",
                                letterSpacing: "1px",
                                textTransform: "uppercase",
                                mb: 1.5,
                            }}
                        >
                            Content Adjustments
                        </Typography>

                        <Box sx={{ display: "flex", gap: 1.5, width: "100%" }}>
                            <CardButton
                                icon="A+"
                                label="Increase Text"
                                onClick={handleIncrease}
                                active={increased}
                            />
                            <CardButton
                                icon="A-"
                                label="Decrease Text"
                                onClick={handleDecrease}
                                active={decreased}
                            />
                        </Box>
                        <Button
                            onClick={() => {
                                resetFont();
                                setIncreased(false);
                                setDecreased(false);
                            }}
                            sx={{
                                width: "100%",
                                background: "#E93333",
                                color: "#fff",
                                fontSize: "14px",
                                fontFamily: "Inter500",
                                marginTop: "10px",
                                textTransform: "capitalize"

                            }}>
                            Reset
                        </Button>
                    </Box>
                </Paper>
            )}

            {/* FAB Button */}
            {/* <Tooltip title="Accessibility" placement="left"> */}
                <IconButton
                aria-label="Accessibility"
                    onClick={() => setOpen((p) => !p)}
                    sx={{
                        width: 56,
                        height: 56,
                        background: "#5469A0",
                        color: "#fff",
                        borderRadius: "50%",
                        boxShadow: "0 4px 16px rgba(26,46,90,0.45)",
                        border: "3px solid #e8eaf0",
                        transition: "transform 0.2s, box-shadow 0.2s",
                        "&:hover": {
                            background: "#5469A0",
                            transform: "scale(1.08)",
                            boxShadow: "0 6px 24px rgba(26,46,90,0.55)",
                        },
                    }}
                >
                    <AccessibilityNewIcon sx={{ fontSize: 28 }} />
                </IconButton>
            {/* </Tooltip> */}
        </Box>
    );
}